<template>
  <Transition :name="animation">
    <div v-if="isActive" class="modal-container">
      <!-- Hidden checkbox that controls modal state -->
      <input 
        type="checkbox" 
        :id="modalId" 
        class="modal-toggle" 
        :checked="isActive"
        @change="handleToggleChange"
      />
      <!-- Modal container -->
      <div ref="modalRef" :class="['modal', nestedClass]" role="dialog">
        <div class="modal-box max-h-[90vh] bg-white" :style="{ maxWidth: newWidth }">
          <component
            :is="component"
            v-if="component"
            v-bind="props"
            v-on="events"
            @close="close"
            :provide-parent="true"
          />
          <div v-else-if="content" v-html="content" />
          <slot v-else />
          <button
            v-if="showX"
            type="button"
            class="btn btn-sm btn-circle absolute right-2 top-2"
            @click="cancel('x')"
          >
            ✕
          </button>
        </div>
        <!-- Modal backdrop that closes modal when clicked -->
        <label 
          v-if="cancelOptions.includes('outside')" 
          class="modal-backdrop" 
          :for="modalId"
        >
          Close
        </label>
      </div>
    </div>
  </Transition>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  onBeforeMount,
  nextTick,
} from "vue";
import { useEventListener } from "@vueuse/core";

// Import helper function or define it directly here
const removeElement = (el) => {
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
};

/**
 * @displayName DModal
 */
export default {
  name: "DModal",
  props: {
    active: Boolean,
    component: [Object, Function],
    content: String,
    programmatic: Boolean,
    props: Object,
    events: Object,
    width: {
      type: [String, Number],
      default: 960,
    },
    hasModalCard: Boolean,
    animation: {
      type: String,
      default: "zoom-out",
    },
    canCancel: {
      type: [Array, Boolean],
      default: () => ["x", "outside", "button", "escape"], // Restore escape to default
    },
    onCancel: {
      type: Function,
      default: () => {},
    },
    scroll: {
      type: String,
      default: "clip",
      validator: (value) => ["clip", "keep"].indexOf(value) >= 0,
    },
  },
  emits: ["close", "update:active"],
  setup(props, { emit, expose }) {
    const isActive = ref(props.active || false);
    const modalRef = ref(null);
    const savedScrollTop = ref(null);
    const modalId = ref(`modal_${Math.random().toString(36).substr(2, 9)}`);
    const newWidth = ref(
      typeof props.width === "number" ? `${props.width}px` : props.width
    );
    // Track nesting level for modals
    const currentOpenModals = ref(
      document.querySelectorAll(".modal input[type='checkbox']:checked").length
    );
    const nestedClass = computed(() => {
      if (currentOpenModals.value === 0) return "";
      if (currentOpenModals.value === 1) return "nested-modal";
      if (currentOpenModals.value >= 2)
        return "nested-modal nested-modal-level-2";
      return "";
    });

    const cancelOptions = computed(() => {
      return typeof props.canCancel === "boolean"
        ? props.canCancel
          ? ["x", "outside", "button", "escape"] // Restore escape to default
          : []
        : props.canCancel;
    });

    const showX = computed(() => {
      return cancelOptions.value.indexOf("x") >= 0;
    });

    // Handle checkbox state changes (from backdrop clicks, etc.)
    const handleToggleChange = (event) => {
      const isChecked = event.target.checked;
      if (!isChecked && isActive.value) {
        // Modal was closed via checkbox (backdrop click)
        cancel("outside");
      }
    };

    // Handle escape key globally
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isActive.value && cancelOptions.value.includes("escape")) {
        // Only close if this is the topmost modal
        const openModals = document.querySelectorAll(".modal input[type='checkbox']:checked");
        if (openModals.length === 0 || openModals[openModals.length - 1].id === modalId.value) {
          event.preventDefault();
          cancel("escape");
        }
      }
    };

    watch(
      () => props.active,
      (value) => {
        if (value !== isActive.value) {
          isActive.value = value;
        }

        if (value) {
          // Update count of open modals before showing this one
          nextTick(() => {
            currentOpenModals.value =
              document.querySelectorAll(".modal input[type='checkbox']:checked").length;

            // Update modal stack levels
            document
              .querySelectorAll(".modal input[type='checkbox']:checked")
              .forEach((checkbox, index, list) => {
                const modal = checkbox.parentElement.querySelector('.modal-box');
                if (modal) {
                  modal.dataset.modalLevel = list.length - index;
                }
              });
          });
        }
      },
      { immediate: true }
    );

    const cancel = (method) => {
      if (!cancelOptions.value.includes(method)) return;
      
      // Call the onCancel callback if provided
      if (typeof props.onCancel === 'function') {
        try {
          props.onCancel(method);
        } catch (error) {
          console.warn('Error in onCancel callback:', error);
        }
      }
      
      // Always close the modal
      close();
    };

    const close = () => {
      // Don't duplicate close logic if dialog already closed
      if (!isActive.value) return;
      
      // Always emit close first to update parent state
      emit("close");
      emit("update:active", false);
      
      // Then update internal state
      isActive.value = false;

      // Update the modal stack levels
      nextTick(() => {
        // Update open modals count
        currentOpenModals.value =
          document.querySelectorAll(".modal input[type='checkbox']:checked").length;

        // Update data-modal-level attributes on remaining modals
        document
          .querySelectorAll(".modal input[type='checkbox']:checked")
          .forEach((checkbox, index, list) => {
            const modal = checkbox.parentElement.querySelector('.modal-box');
            if (modal) {
              modal.dataset.modalLevel = list.length - index;
            }
          });
      });

      // Timeout for the animation complete before destroying
      if (props.programmatic) {
        setTimeout(() => {
          // Update count of modals when closing
          currentOpenModals.value =
            document.querySelectorAll(".modal input[type='checkbox']:checked").length;

          // Find our specific modal element
          if (modalRef.value && modalRef.value.parentNode) {
            removeElement(modalRef.value);
          }
        }, 150);
      }
    };

    onBeforeMount(() => {
      // We need to wait for the DOM to be created
      if (props.programmatic) {
        nextTick(() => {
          const element = document.getElementById("modal-placeholder");
          if (!element && document.body) {
            document.body.appendChild(document.createElement("div"));
          }
        });
      }
    });

    onMounted(() => {
      if (typeof window !== "undefined") {
        // Add global escape key handler
        document.addEventListener("keydown", handleEscapeKey);

        // Update count of current open modals for nesting detection
        currentOpenModals.value =
          document.querySelectorAll(".modal input[type='checkbox']:checked").length;
      }

      if (props.programmatic) {
        isActive.value = true;
        nextTick(() => {
          // Update count before showing the modal
          currentOpenModals.value =
            document.querySelectorAll(".modal input[type='checkbox']:checked").length;
        });
      }
    });

    onBeforeUnmount(() => {
      // Clean up event listeners
      if (typeof window !== "undefined") {
        document.removeEventListener("keydown", handleEscapeKey);
      }
    });

    // Expose methods for programmatic usage
    expose({
      close,
    });
    return {
      isActive,
      modalRef,
      modalId,
      newWidth,
      showX,
      cancelOptions,
      nestedClass,
      cancel,
      close,
      handleToggleChange,
    };
  },
};
</script>

<style scoped>
/* Style for nested modals */
dialog.modal.nested-modal {
  /* Increment z-index for nested modals */
  z-index: 200;

  /* Make nested modals slightly smaller with a margin */
  /* width: calc(100% - 40px); */
  /* max-width: calc(100% - 40px); */
  /* margin: 20px auto; */
}

/* Add a third level just in case */
dialog.modal.nested-modal-level-2 {
  z-index: 300;

  /* Make third level modals even smaller */
  /* width: calc(100% - 80px);
  max-width: calc(100% - 80px);
  margin: 40px auto; */
}

dialog.modal.nested-modal-level-2::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
